/**
 * Students Service — 단순캐시(옵션2) 서버 권위 CRUD
 *
 * ISSUE_167: Supabase = SSOT. 클라 캐시·낙관 UI 는 React Query(core/query/useStudents)가 담당.
 * 이 서비스는 **순수 Supabase 호출**만 — pendingMutations 큐·localFirstMutation·readThroughCache 없음.
 *
 * Supports two modes:
 * - Personal mode (scratch): owner_id 필터 (academy_id is null)
 * - Academy mode: academy_id 필터
 *
 * @module studentsService
 */

import { supabase, isSupabaseConfigured } from "~/shared/lib/supabase";
import { mutateWithAuthRetry, paginatedQuery } from "~/core/api/executeWithAuthRetry";
import type { Database } from "~/types/database.types";
import type { WorkspaceContext } from "~/services/sessions/sessionsService";
import { SyncError } from "~/core/sync/SyncError";

type DbStudentRow = Database["public"]["Tables"]["students"]["Row"];
type DbStudentInsert = Database["public"]["Tables"]["students"]["Insert"];

// ============================================================================
// TYPES
// ============================================================================

export interface StudentFromDb {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  school?: string;
  schoolId?: string;
  grade?: string;
  guardianName?: string;
  guardianPhone?: string;
  status: 'active' | 'paused' | 'waiting' | 'graduated' | 'quit';
  memo?: string;
  enrolledAt?: string;
  withdrawnAt?: string;
}

export interface CreateStudentInput {
  name: string;
  phone?: string | undefined;
  email?: string | undefined;
  school?: string | undefined;
  schoolId?: string | undefined;
  grade?: string | undefined;
  guardianName?: string | undefined;
  guardianPhone?: string | undefined;
  memo?: string | undefined;
  // 승격 백필이 기존 학생의 상태/입퇴원 이력을 보존하도록 전달(일반 생성 시 미지정 → 기본값). (Phase 5.2')
  status?: StudentFromDb["status"] | undefined;
  enrolledAt?: string | undefined;
  withdrawnAt?: string | undefined;
  workspace: WorkspaceContext;
}

/**
 * 부분 패치 — 낙관 캐시 머지와 Supabase update 양쪽에 동일 사용.
 * null = 컬럼 비우기(클리어, 예: 복원 시 withdrawnAt). undefined = 미변경.
 */
export type StudentPatch = {
  [K in keyof Omit<StudentFromDb, "id">]?: StudentFromDb[K] | null;
};

// ============================================================================
// HELPERS
// ============================================================================

/** Convert database row to StudentFromDb */
function fromDbFormat(row: DbStudentRow): StudentFromDb {
  const result: StudentFromDb = {
    id: row.id,
    name: row.name,
    status: (row.status || 'active') as StudentFromDb['status'],
  };

  // Only add optional fields if they exist (exactOptionalPropertyTypes)
  if (row.phone) result.phone = row.phone;
  if (row.email) result.email = row.email;
  if (row.school) result.school = row.school;
  if (row.school_id) result.schoolId = row.school_id;
  if (row.grade) result.grade = row.grade;
  if (row.guardian_name) result.guardianName = row.guardian_name;
  if (row.guardian_phone) result.guardianPhone = row.guardian_phone;
  if (row.memo) result.memo = row.memo;
  if (row.enrolled_at) result.enrolledAt = row.enrolled_at;
  if (row.withdrawn_at) result.withdrawnAt = row.withdrawn_at;

  return result;
}

export function getStudentCacheKey(workspace: WorkspaceContext): string {
  return workspace.academyId!;
}

/**
 * StudentPatch 를 캐시 row 에 적용 (낙관 머지).
 * null/undefined 값은 키 삭제(exactOptionalPropertyTypes 회피).
 */
export function applyStudentPatch(row: StudentFromDb, patch: StudentPatch): StudentFromDb {
  const next = { ...row } as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next as unknown as StudentFromDb;
}

// ============================================================================
// READ
// ============================================================================

/** Fetch students for a workspace (서버 직조회, paginatedQuery 로 max_rows 우회) */
export async function fetchStudentsForWorkspace(
  workspace: WorkspaceContext
): Promise<StudentFromDb[]> {
  if (!isSupabaseConfigured() || !supabase) {
    return [];
  }

  const data = await paginatedQuery<DbStudentRow>((from, to) =>
    supabase!
      .from("students")
      .select("*")
      .eq("academy_id", workspace.academyId!)
      .order("name", { ascending: true })
      .range(from, to),
  );

  return data.map(fromDbFormat);
}

// ============================================================================
// WRITE — 순수 Supabase 호출 (서버 권위). 낙관/큐는 React Query 가 담당.
// ============================================================================

/**
 * 학생 생성 — Supabase upsert.
 * 멱등(onConflict id): 타임아웃 재시도(같은 클라 id) 시 23505 영구실패 방지 + 승격 백필 재시도 안전.
 */
export async function createStudentRemote(
  input: CreateStudentInput & { id: string }
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }

  const dbData: DbStudentInsert = {
    id: input.id,
    name: input.name,
    phone: input.phone || null,
    email: input.email || null,
    school: input.school || null,
    school_id: input.schoolId || null,
    grade: input.grade || null,
    guardian_name: input.guardianName || null,
    guardian_phone: input.guardianPhone || null,
    memo: input.memo || null,
    // 승격 백필이 status/입퇴원 이력을 실어 보내면 보존. 일반 생성은 미지정 → 기본값. (Phase 5.2')
    status: input.status ?? 'active',
    enrolled_at: input.enrolledAt ?? null,
    withdrawn_at: input.withdrawnAt ?? null,
    owner_id: null,
    academy_id: input.workspace?.academyId ?? null,
  };

  const { error } = await mutateWithAuthRetry(() =>
    supabase!.from("students").upsert(dbData, { onConflict: "id" }).select("id").single()
  );
  if (error) throw SyncError.fromSupabase(error);
}

/** 학생 수정 — Supabase update (camelCase patch → snake_case 컬럼). null = 컬럼 클리어. */
export async function updateStudentRemote(
  id: string,
  patch: StudentPatch,
  _workspace?: WorkspaceContext,
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }

  const col: Record<keyof StudentPatch, string> = {
    name: "name",
    phone: "phone",
    email: "email",
    school: "school",
    schoolId: "school_id",
    grade: "grade",
    guardianName: "guardian_name",
    guardianPhone: "guardian_phone",
    memo: "memo",
    status: "status",
    enrolledAt: "enrolled_at",
    withdrawnAt: "withdrawn_at",
  };

  const dbUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch) as [keyof StudentPatch, unknown][]) {
    if (value === undefined) continue;
    // 빈 문자열은 NULL 로 정규화(기존 동작 보존). status 는 enum 이라 그대로.
    dbUpdates[col[key]] = value === "" && key !== "status" ? null : value;
  }

  if (Object.keys(dbUpdates).length === 0) return;

  const { error } = await mutateWithAuthRetry(() =>
    supabase!.from("students").update(dbUpdates).eq("id", id)
  );
  if (error) throw SyncError.fromSupabase(error);
}

export default {
  fetchStudentsForWorkspace,
  createStudentRemote,
  updateStudentRemote,
};
